/**
 * Demo-safe masking, active only while a non-default brand is on.
 *
 * White-label exists to show the product to someone who should not learn whose
 * product it is. A re-skinned logo does not achieve that on its own: the
 * customer's identity leaks through every seller email, every timeline byline
 * and every owner column. So the brand switch also anonymises the internal
 * team.
 *
 * What is masked: the people who work the leads — owners, closers, note
 * authors, timeline bylines, the colleague pickers. What is NOT masked: the
 * prospect companies and their contacts. Those are the content of the demo, and
 * blanking them would leave nothing to look at.
 *
 * This is presentation-only. The API still returns real addresses and they are
 * visible in devtools, so this is a screen-sharing measure, not a security
 * boundary — treat it as such.
 */

// Labels come from the roster, sorted, so they are collision-free: with a hash
// alone two of the four real BDRs landed on the same number, which in a demo
// merges two people and misattributes their work.
//
// The fallback hash is only for an address the roster does not know — a
// deactivated user still named in an old timeline entry. 4096 buckets keeps a
// collision there vanishingly unlikely, and it is rendered in base-36 so the
// label stays short.
function fallbackLabel(seed: string): string {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 16777619) >>> 0
  }
  return `BDR ${(h % 4096).toString(36)}`
}

/**
 * A neutral label for one member of the internal team.
 *
 * `roster` is every known address, in any order — this sorts it, so the same
 * person is the same number on every screen and across reloads.
 */
export function maskPerson(value?: string | null, roster?: readonly string[]): string {
  const v = (value || '').trim().toLowerCase()
  if (!v) return '—'
  if (roster && roster.length) {
    const i = [...roster].map(e => e.toLowerCase()).sort().indexOf(v)
    if (i >= 0) return `BDR ${i + 1}`
  }
  return fallbackLabel(v)
}

/**
 * Names of things that are the customer rather than the product. Swapped for
 * the active brand's own wording so a demo does not show a competitor's
 * infrastructure by name.
 */
export function maskSystem(value: string, brandName: string): string {
  return value.replace(/yllw/gi, brandName.toLowerCase())
}

/**
 * The AI brief, with the customer's name taken out of every prose field.
 *
 * Done to the object once rather than at each render site: the brief has five
 * free-text fields plus a signal summary each, all model-written, and any of
 * them can name the customer. Censoring them one render at a time is how you
 * ship a demo that hides the name in four places and prints it in the fifth.
 *
 * Contacts are untouched on purpose — those are the prospect's people, which is
 * the content being demonstrated, not the identity being hidden.
 */
export function censorHandover<T extends {
  opportunity: string
  pitchAngle: string
  timing: string
  esg?: string
  reasoning: string
  signals: { summary: string }[]
}>(h: T, brandName: string): T {
  const m = (v: string) => maskSystem(v, brandName)
  return {
    ...h,
    opportunity: m(h.opportunity),
    pitchAngle: m(h.pitchAngle),
    timing: m(h.timing),
    esg: h.esg ? m(h.esg) : h.esg,
    reasoning: m(h.reasoning),
    signals: h.signals.map(s => ({ ...s, summary: m(s.summary) })),
  }
}
